import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  createTheme,
  ThemeProvider,
  Box,
  Toolbar,
  Container,
  Grid,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
  SelectChangeEvent,
  CircularProgress,
} from "@mui/material";

import { Add, ArrowBack } from "@mui/icons-material";
import {
  createColorVehicle,
  createColorVehicleCompany,
  createModelYearVehicle,
  createModelYearVehicleCompany,
  createVehicle,
  createVehicleWithClient,
  fetchVehicleByVin,
  fetchVehicleByVinApiConfig,
  getColorsVehicle,
  getColorsVehicleCompany,
  getModelsVehicle,
  getModelsVehiclesCompany,
  getModelYear,
  getModelYearVehiclesCompany,
  getStorageLocation,
  getStorageLocationCompany,
  kmPhoto,
  radioPhoto,
} from "../../../api/vehicleApi";
import {
  ColorData,
  CreateVehicleData,
  Models,
  StorageLocation,
  YearModel,
} from "../../../types/vehicleData";
import FormTextField from "./formTextField";
import CreateModal from "./createModal";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import {
  subscribeToChannel,
  triggerEvent,
} from "../../../services/pusherService";

const defaultTheme = createTheme({
  typography: {
    fontFamily: "Poppins, sans-serif",
  },
});

const NovoVeiculo: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("token");
  const canal = localStorage.getItem("canal");
  const { clientId } = useParams<{ clientId: string }>();
  const [digitosChassi, setDigitosChassi] = useState<number>(17);
  const [corOptions, setCorOptions] = useState<
    { id: number; descricao_cor: string }[]
  >([]);
  const [modelOptions, setModelOptions] = useState<
    { id: number; descricao_modelo: string }[]
  >([]);
  const [yearOptions, setYearOptions] = useState<
    { id: number; descricao_ano_modelo: string }[]
  >([]);
  const [storageOptions, setStorageOptions] = useState<
    { id: number; descricao_local: string }[]
  >([]);
  const [formValues, setFormValues] = useState({
    "V.I.N": "",
    Placa: "",
    Modelo: "",
    Cor: "",
    KM: "",
    "Código de Rádio": "",
    "Ano / Modelo": "",
    "Data Armazenagem": "",
    "Local Armazenagem": "",
    "KM Final": "",
    "Data de Entrega": "",
  });
  const orderedFields = [
    "V.I.N",
    "Placa",
    "Modelo",
    "Cor",
    "Ano / Modelo",
    "Data Armazenagem",
    "Local Armazenagem",
    "KM Final",
    "Data de Entrega",
    "KM",
    "Código de Rádio",
  ];
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [capturedImages, setCapturedImages] = useState<string[]>([]);
  const [radioCapturedImages, setRadioCapturedImages] = useState<string[]>([]);
  const [isPusherData, setIsPusherData] = useState(false);
  const location = useLocation();
  const [podeCadastrarModelo, setPodeCadastrarModelo] = useState(true);
  const [hasApiConfig, setHasApiConfig] = useState(false);
  const [foundVimCar, setFoundVimCar] = useState<any | null>(null);

  const getQueryParam = (param: string) => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get(param);
  };

  const companyId = getQueryParam("idEmpresa");

  useEffect(() => {
    const config = localStorage.getItem("configUsuario");
    if (config) {
      const parsed = JSON.parse(config);
      setPodeCadastrarModelo(parsed.CadastroModelo === "Sim");
      if (parsed.DigitosChassi) {
        setDigitosChassi(Number(parsed.DigitosChassi));
      }
    }
  }, []);

  useEffect(() => {
    const userDataString = localStorage.getItem("userData");
    if (userDataString) {
      try {
        const parsed = JSON.parse(userDataString);
        if (parsed.ApiConfig) {
          setHasApiConfig(true);
        }

        if (parsed.digito_chassi) {
          setDigitosChassi(Number(parsed.digito_chassi));
        }
      } catch (e) {
        console.error("Erro ao ler userData:", e);
      }
    }
  }, []);

  useEffect(() => {
    const canal = localStorage.getItem("canal");
    const eventsString = localStorage.getItem("events");
    const events = eventsString ? JSON.parse(eventsString) : [];

    const hasVehicleEvent = events.some(
      (event: any) => event.categoria === "veiculo"
    );

    if (canal && hasVehicleEvent) {
      setIsPusherData(true);
    } else {
      setIsPusherData(false);
    }
  }, []);

  const fetchColors = async () => {
    try {
      const token = localStorage.getItem("token");
      const userData = localStorage.getItem("userData");
      if (token) {
        let colorsData: ColorData[] = [];
        let modelsData: Models[] = [];
        let yearsData: YearModel[] = [];
        let storageData: StorageLocation[] = [];

        if (userData) {
          const parsedUserData = JSON.parse(userData);
          if (parsedUserData.admin && companyId) {
            colorsData = await getColorsVehicleCompany(token, companyId);
            modelsData = await getModelsVehiclesCompany(token, companyId);
            yearsData = await getModelYearVehiclesCompany(token, companyId);
            storageData = await getStorageLocationCompany(token, companyId);
          } else {
            colorsData = await getColorsVehicle(token);
            modelsData = await getModelsVehicle(token);
            yearsData = await getModelYear(token);
            storageData = await getStorageLocation(token);
          }
        } else {
          colorsData = await getColorsVehicle(token);
          modelsData = await getModelsVehicle(token);
          yearsData = await getModelYear(token);
          storageData = await getStorageLocation(token);
        }

        const colors = colorsData.map((color) => ({
          id: color.id,
          descricao_cor: color.descricao_cor,
        }));

        const models = modelsData.map((model) => ({
          id: model.id,
          descricao_modelo: model.descricao_modelo,
        }));

        const years = yearsData.map((year) => ({
          id: year.id,
          descricao_ano_modelo: year.descricao_ano_modelo,
        }));

        const storageLocation = storageData.map((location) => ({
          id: location.id,
          descricao_local: location.descricao_local,
        }));

        setCorOptions(colors);
        setModelOptions(models);
        setYearOptions(years);
        setStorageOptions(storageLocation);
      }
    } catch (error) {
      console.error("Erro:", error);
    }
  };

  useEffect(() => {
    fetchColors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | { name?: string; value: unknown }
    >
  ) => {
    if (typeof event.target === "object" && event.target.name) {
      const { name, value } = event.target;
      setFormValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));
    }
  };

  const handleAxiosError = (error: any) => {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.duplicate_error ||
        "Não foi possível cadastrar o veículo ou enviar a foto do KM!";
      toast.error(`${errorMessage}`);
    } else {
      toast.error("Erro inesperado. Por favor, tente novamente.");
    }
  };

  useEffect(() => {
    const canal = localStorage.getItem("canal");
    const eventsString = localStorage.getItem("events");
    const events = eventsString ? JSON.parse(eventsString) : [];
    const hasVehicleEvent = events.some(
      (event: any) => event.categoria === "veiculo"
    );

    if (formValues["V.I.N"].length === 17 && canal && hasVehicleEvent) {
      const buscarVeiculo = async () => {
        try {
          if (!token) {
            throw new Error("Token de autenticação não está disponível.");
          }

          const vehicleData = await fetchVehicleByVin(
            formValues["V.I.N"],
            token
          );

          if (vehicleData) {
            triggerEvent(canal, "veiculo", vehicleData);
          }
        } catch (error) {
          console.error("Erro ao buscar dados do veículo:", error);
        }
      };

      buscarVeiculo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues["V.I.N"], token]);

  const createNewColor = async (newColor: string) => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("userData");

    if (!token || !userData) {
      toast.error("Autenticação necessária");
      return;
    }

    const parsedUserData = JSON.parse(userData);
    const isAdmin = parsedUserData.admin && companyId;

    if (isAdmin) {
      await createColorVehicleCompany(
        token,
        { descricao_cor: newColor, id_empresa: parseInt(companyId) },
        companyId
      );
      const colorsData = await getColorsVehicleCompany(token, companyId);
      const colors = colorsData.map((color: ColorData) => ({
        id: color.id,
        descricao_cor: color.descricao_cor,
      }));
      setCorOptions(colors);
    } else {
      await createColorVehicle(token, { descricao_cor: newColor });
      const colorsData = await getColorsVehicle(token);
      const colors = colorsData.map((color: ColorData) => ({
        id: color.id,
        descricao_cor: color.descricao_cor,
      }));
      setCorOptions(colors);
    }
  };

  useEffect(() => {
    if (!hasApiConfig) return;

    const vin = formValues["V.I.N"];

    if (!vin || vin.length !== 17) return;
    if (!token) return;

    const buscarVeiculoApiConfig = async () => {
      try {
        setLoading(true);
        const result = await fetchVehicleByVinApiConfig(vin, token);

        if (!result || result.encontrado === false) {
          toast.warning("Veículo não encontrado na API.");
          setFoundVimCar(null);
          return;
        }

        const dados = result.dados;

        setFoundVimCar(dados);

        const matchingColorById = corOptions.find((c) => c.id === dados.id_cor);

        const matchingColor =
          matchingColorById ||
          corOptions.find(
            (c) =>
              c.descricao_cor.toLowerCase() ===
              (dados.descricao_cor || "").toLowerCase()
          );

        if (!matchingColor && dados.descricao_cor && corOptions.length > 0) {
          createNewColor(dados.descricao_cor);
          return;
        }

        const matchingModelById = modelOptions.find(
          (m) => m.id === dados.id_modelo
        );

        const matchingModel =
          matchingModelById ||
          modelOptions.find(
            (m) =>
              m.descricao_modelo.toLowerCase() ===
              (dados.descricao_modelo || "").toLowerCase()
          );

        const matchingYearById = yearOptions.find(
          (y) => y.id === dados.id_anomodelo
        );

        const matchingYearByDescricao = yearOptions.find(
          (y) =>
            y.descricao_ano_modelo.trim().toLowerCase() ===
            (dados.descricao_ano_modelo || "").trim().toLowerCase()
        );

        const matchingYear = matchingYearById || matchingYearByDescricao;

        setFormValues((prev) => ({
          ...prev,
          "V.I.N": dados.chassi_veiculo || prev["V.I.N"],
          Placa: dados.placa_veiculo || prev.Placa,
          KM:
            dados.km_veiculo !== undefined && dados.km_veiculo !== null
              ? String(dados.km_veiculo)
              : prev.KM,
          Modelo: matchingModel ? String(matchingModel.id) : prev.Modelo,
          Cor: matchingColor ? String(matchingColor.id) : prev.Cor,
          "Ano / Modelo": matchingYear
            ? String(matchingYear.id)
            : prev["Ano / Modelo"],
        }));
      } catch (error) {
        console.error("Erro ao buscar veículo pela API (ApiConfig):", error);
        toast.error("Erro ao buscar dados do veículo pela API.");
      } finally {
        setLoading(false);
      }
    };

    buscarVeiculoApiConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues["V.I.N"],
    hasApiConfig,
    token,
    corOptions,
    modelOptions,
    yearOptions,
  ]);

  useEffect(() => {
    const canal = localStorage.getItem("canal");
    const eventsString = localStorage.getItem("events");
    const events = eventsString ? JSON.parse(eventsString) : [];
    const hasVehicleEvent = events.some(
      (event: any) => event.categoria === "veiculo"
    );

    if (!canal || !hasVehicleEvent) {
      console.warn(
        "[Veículo] Canal ou evento veículo não definido - assinatura cancelada"
      );
      return;
    }

    const unsubscribe = subscribeToChannel(
      canal,
      "veiculo",
      async (data: any) => {
        try {
          let parsedData = data;
          if (typeof data === "string") {
            parsedData = JSON.parse(data);
          }

          const item = Array.isArray(parsedData) ? parsedData[0] : parsedData;

          if (!item) {
            console.warn("[Veículo] Dados recebidos são inválidos ou vazios");
            return;
          }

          const matchingModel = modelOptions.find(
            (model) =>
              model.descricao_modelo.toLowerCase() ===
              item.DESCRICAO_MODELO?.toLowerCase()
          );

          if (!matchingModel) {
            console.warn(
              "[Veículo] Modelo não encontrado:",
              item.DESCRICAO_MODELO
            );
            toast.warn(
              "Modelo não encontrado, entrar em contato com o suporte!"
            );
          }

          const matchingColor = corOptions.find(
            (color) =>
              color.descricao_cor.toLowerCase() ===
              item.DESCRICAO_COR?.toLowerCase()
          );

          const matchingAno = yearOptions.find(
            (year) => year.descricao_ano_modelo === item.DESCRICAO_ANO_MODELO
          );

          if (!matchingColor) {
            const token = localStorage.getItem("token");
            const newColor = {
              descricao_cor: item.DESCRICAO_COR,
            };

            try {
              if (!token) {
                throw new Error("Token de autenticação não está disponível.");
              }

              const userData = localStorage.getItem("userData");
              if (token && userData) {
                const parsedUserData = JSON.parse(userData);
                const isAdmin = parsedUserData.admin && companyId;

                if (isAdmin) {
                  await createColorVehicleCompany(
                    token,
                    {
                      descricao_cor: newColor.descricao_cor,
                      id_empresa: parseInt(companyId),
                    },
                    companyId
                  );
                } else {
                  await createColorVehicle(token, newColor);
                }

                const colorsData = isAdmin
                  ? await getColorsVehicleCompany(token, companyId)
                  : await getColorsVehicle(token);

                const colors = colorsData.map((color: ColorData) => ({
                  id: color.id,
                  descricao_cor: color.descricao_cor,
                }));

                setCorOptions(colors);

                const newColorId = colors.find(
                  (c: ColorData) =>
                    c.descricao_cor.toLowerCase() ===
                    newColor.descricao_cor.toLowerCase()
                )?.id;

                if (newColorId) {
                  setFormValues((prevValues) => ({
                    ...prevValues,
                    Cor: String(newColorId),
                  }));
                }
              }
            } catch (error) {
              console.error("[Veículo] Erro ao criar nova cor:", error);
            }
          } else {
            setFormValues((prevValues) => ({
              ...prevValues,
              Cor: String(matchingColor.id),
            }));
          }

          if (!matchingAno) {
            const token = localStorage.getItem("token");
            const newAno = {
              descricao_ano_modelo: item.DESCRICAO_ANO_MODELO,
            };

            try {
              if (!token) {
                throw new Error("Token de autenticação não está disponível.");
              }

              const userData = localStorage.getItem("userData");
              if (token && userData) {
                const parsedUserData = JSON.parse(userData);
                const isAdmin = parsedUserData.admin && companyId;

                if (isAdmin) {
                  await createModelYearVehicleCompany(
                    token,
                    {
                      descricao_ano_modelo: newAno.descricao_ano_modelo,
                      id_empresa: parseInt(companyId),
                    },
                    companyId
                  );
                } else {
                  await createModelYearVehicle(token, newAno);
                }

                const yearsData = isAdmin
                  ? await getModelYearVehiclesCompany(token, companyId)
                  : await getModelYear(token);

                const years = yearsData.map((year: YearModel) => ({
                  id: year.id,
                  descricao_ano_modelo: year.descricao_ano_modelo,
                }));

                setYearOptions(years);

                const newAnoId = years.find(
                  (y: YearModel) =>
                    y.descricao_ano_modelo === newAno.descricao_ano_modelo
                )?.id;

                if (newAnoId) {
                  setFormValues((prevValues) => ({
                    ...prevValues,
                    "Ano / Modelo": String(newAnoId),
                  }));
                }
              }
            } catch (error) {
              console.error("[Veículo] Erro ao criar novo ano/modelo:", error);
            }
          } else {
            setFormValues((prevValues) => ({
              ...prevValues,
              "Ano / Modelo": String(matchingAno.id),
            }));
          }

          setFormValues((prevValues) => {
            if (prevValues["V.I.N"] === item.CHASSI_VEICULO) return prevValues;
            return {
              ...prevValues,
              "V.I.N": item.CHASSI_VEICULO || "",
              Modelo: matchingModel ? String(matchingModel.id) : "",
              KM: item.KM_VEICULO || "",
              "Código de Rádio": item.CODIGO_RADIO_VEICULO || "",
              "Data Armazenagem": item.DATA_RECEBIMENTO_VEICULO || "",
            };
          });

          setIsPusherData(true);
        } catch (error) {
          console.error("[Veículo] Erro ao processar os dados do Pusher:", {
            error,
            dadosRecebidos: data,
            tipoDados: typeof data,
          });
        }
      }
    );

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelOptions, corOptions, yearOptions, canal]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    if (!formValues["V.I.N"] && !formValues["Placa"]) {
      toast.error("Preencha pelo menos o V.I.N ou a Placa para continuar.");
      setLoading(false);
      return;
    }

    if (formValues["Placa"] && formValues["Placa"].length !== 7) {
      toast.error("A placa deve ter exatamente 7 dígitos.");
      setLoading(false);
      return;
    }

    try {
      if (token) {
        const requiredFields = [
          "Modelo",
          "Cor",
          "KM",
          "Ano / Modelo",
          "Data Armazenagem",
          "Local Armazenagem",
        ];
        const missingFields = requiredFields.filter(
          (fieldName) => !formValues[fieldName as keyof typeof formValues]
        );

        if (missingFields.length > 0) {
          toast.error("É necessário preencher todos os campos obrigatórios.");
          return;
        }

        if (formValues["V.I.N"]) {
          const vinLength = formValues["V.I.N"].length;

          if (vinLength < digitosChassi || vinLength > 17) {
            toast.error(
              `O campo V.I.N deve ter entre ${digitosChassi} e 17 dígitos.`
            );
            setLoading(false);
            return;
          }
        }

        if (clientId) {
          const corId = parseInt(formValues["Cor"]);
          const modeloId = parseInt(formValues["Modelo"]);
          const anoModeloId = parseInt(formValues["Ano / Modelo"]);
          const storageLocationId = parseInt(formValues["Local Armazenagem"]);
          const companyIdValue = companyId ?? "";
          if (!isNaN(corId) && !isNaN(modeloId) && !isNaN(anoModeloId)) {
            const vehicleData: CreateVehicleData = {
              chassi_veiculo: formValues["V.I.N"],
              placa_veiculo: formValues["Placa"],
              data_recebimento_veiculo: formValues["Data de Entrega"],
              km_veiculo: parseInt(formValues["KM"]),
              id_local: storageLocationId,
              id_cor: corId,
              id_modelo: modeloId,
              codigo_radio_veiculo: formValues["Código de Rádio"],
              ano_veiculo: parseInt(formValues["Ano / Modelo"]),
              data_armazenagem_veiculo: formValues["Data Armazenagem"],
              km_final_veiculo: parseInt(formValues["KM Final"]),
              id_ano_modelo: anoModeloId,
              descricao_cor: formValues["Cor"],
              descricao_modelo: formValues["Modelo"],
              descricao_ano_modelo: formValues["Ano / Modelo"],
              descricao_local: formValues["Local Armazenagem"],
              acoes: [],
              id_empresa: parseInt(companyIdValue),
            };

            const response = await createVehicleWithClient(
              vehicleData,
              token,
              clientId
            );
            const veiculoId = response.id_veiculo;

            const uploadPromises = capturedImages.map(async (image) => {
              try {
                await kmPhoto(veiculoId, image, token);
              } catch (error) {}
            });

            const uploadRadioPromises = radioCapturedImages.map(
              async (image) => {
                try {
                  await radioPhoto(veiculoId, image, token);
                } catch (error) {
                  console.error(
                    "Erro ao enviar a foto do Código de Rádio:",
                    error
                  );
                }
              }
            );

            const allUploadPromises = [
              ...uploadPromises,
              ...uploadRadioPromises,
            ];
            await Promise.all(allUploadPromises);

            setCapturedImages([]);
            setRadioCapturedImages([]);

            toast.success("Veículo cadastrado com sucesso!");
            navigate("/veiculos");
          }
        } else {
          const corId = parseInt(formValues["Cor"]);
          const modeloId = parseInt(formValues["Modelo"]);
          const anoModeloId = parseInt(formValues["Ano / Modelo"]);
          const storageLocationId = parseInt(formValues["Local Armazenagem"]);
          const companyIdValue = companyId ?? "";
          if (!isNaN(corId) && !isNaN(modeloId) && !isNaN(anoModeloId)) {
            const vehicleData: CreateVehicleData = {
              chassi_veiculo: formValues["V.I.N"],
              placa_veiculo: formValues["Placa"],
              data_recebimento_veiculo: formValues["Data de Entrega"],
              km_veiculo: parseInt(formValues["KM"]),
              id_local: storageLocationId,
              id_cor: corId,
              id_modelo: modeloId,
              codigo_radio_veiculo: formValues["Código de Rádio"],
              ano_veiculo: parseInt(formValues["Ano / Modelo"]),
              data_armazenagem_veiculo: formValues["Data Armazenagem"],
              km_final_veiculo: parseInt(formValues["KM Final"]),
              id_ano_modelo: anoModeloId,
              descricao_cor: formValues["Cor"],
              descricao_modelo: formValues["Modelo"],
              descricao_ano_modelo: formValues["Ano / Modelo"],
              descricao_local: formValues["Local Armazenagem"],
              acoes: [],
              id_empresa: parseInt(companyIdValue),
            };

            const response = await createVehicle(token, vehicleData);
            const veiculoId = response.id;

            if (veiculoId) {
              const uploadPromises = capturedImages.map(async (image) => {
                try {
                  await kmPhoto(veiculoId, image, token);
                } catch (error) {}
              });

              const uploadRadioPromises = radioCapturedImages.map(
                async (image) => {
                  try {
                    await radioPhoto(veiculoId, image, token);
                  } catch (error) {
                    console.error(
                      "Erro ao enviar a foto do Código de Rádio:",
                      error
                    );
                    toast.error("Erro ao enviar a foto do Código de Rádio");
                  }
                }
              );

              const allUploadPromises = [
                ...uploadPromises,
                ...uploadRadioPromises,
              ];

              await Promise.all(allUploadPromises);

              setCapturedImages([]);
              setRadioCapturedImages([]);

              toast.success("Veículo cadastrado com sucesso!");
              navigate("/veiculos");
            } else {
              toast.error("ID do veículo não disponível após o cadastro.");
            }
          }
        }
      }
    } catch (error) {
      console.error("Erro:", error);
      handleAxiosError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setOpenModal(null);
  };

  const handleCreateModalOpen = (fieldName: string) => {
    setOpenModal(fieldName);
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ mb: 1, mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  cursor: "pointer",
                  color: "red",
                },
              }}
              onClick={() => navigate("/veiculos")}
            >
              <IconButton
                edge="start"
                color="inherit"
                aria-label="voltar"
                sx={{ color: "inherit" }}
              >
                <ArrowBack />
              </IconButton>
              <Typography
                variant="h6"
                component="div"
                sx={{ color: "inherit", mt: 0.2 }}
              >
                Voltar
              </Typography>
            </Box>
          </Toolbar>
          <Container sx={{ mb: 4 }} maxWidth={"lg"}>
            <Box component="form" noValidate>
              <Typography
                variant="h5"
                fontStyle={"inherit"}
                sx={{ fontWeight: 400 }}
              >
                Cadastrar Veículo
                <Divider
                  variant="middle"
                  sx={{
                    width: 120,
                    backgroundColor: "red",
                    height: 2,
                    marginLeft: 0,
                  }}
                />
              </Typography>
              <Grid container spacing={3} mt={-2} p={2}>
                {orderedFields.map((fieldName) => (
                  <Grid item xs={12} sm={6} key={fieldName}>
                    {fieldName === "Cor" ||
                    fieldName === "Modelo" ||
                    fieldName === "Local Armazenagem" ||
                    fieldName === "Ano / Modelo" ? (
                      <FormControl fullWidth sx={{ marginTop: -2 }}>
                        <Typography ml={1} mt={2} mb={1}>
                          {fieldName}
                          <Typography component="span" color="error">
                            *
                          </Typography>
                          {fieldName === "Modelo" && foundVimCar && (
                            <Box
                              display={"flex"}
                              flexDirection={"column"}
                              p={1}
                              border={"1px solid #ccc"}
                              borderRadius={2}
                              gap={0.5}
                            >
                              <Typography fontSize={"14px"}>
                                Modelo encontrado:
                              </Typography>
                              <Typography fontSize={"16px"} fontWeight={600}>
                                {foundVimCar.LSM}
                              </Typography>
                            </Box>
                          )}
                        </Typography>
                        <Select
                          labelId={`${fieldName}-label`}
                          id={fieldName}
                          value={
                            formValues[fieldName]
                              ? Number(formValues[fieldName])
                              : ""
                          }
                          placeholder={`Selecione o ${fieldName}`}
                          onChange={(event: SelectChangeEvent<number>) => {
                            const { value } = event.target;
                            setFormValues((prevValues) => ({
                              ...prevValues,
                              [fieldName]: value,
                            }));
                          }}
                          displayEmpty={true}
                          disabled={
                            (fieldName === "Modelo" ||
                              fieldName === "Cor" ||
                              fieldName === "Ano / Modelo") &&
                            isPusherData
                          }
                          sx={{
                            "& .MuiSelect-root": {
                              borderColor: "#303030",
                              borderRadius: 3,
                              boxShadow: 6,

                              "&:focus": {
                                backgroundColor: "#ffddec",
                                borderColor: "brown",
                              },
                              "&:before": {
                                borderColor: "orange",
                              },
                              "&:after": {
                                borderColor: "green",
                              },
                            },

                            ".MuiOutlinedInput-notchedOutline": {
                              borderColor: "#303030",
                              borderRadius: 3,
                              boxShadow: 6,
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#303030",
                              color: "#303030",
                              borderWidth: "2px",
                            },
                          }}
                        >
                          {(fieldName !== "Modelo" ||
                            (fieldName === "Modelo" &&
                              podeCadastrarModelo)) && (
                            <MenuItem
                              onClick={() => handleCreateModalOpen(fieldName)}
                            >
                              <IconButton size="small" sx={{ mr: 1 }}>
                                <Add />
                              </IconButton>
                              Cadastrar {fieldName}
                            </MenuItem>
                          )}
                          <MenuItem value="" disabled>
                            Selecione {`${fieldName}`}
                          </MenuItem>
                          {fieldName === "Cor" &&
                            corOptions.map((option, index) => (
                              <MenuItem key={index} value={option.id}>
                                {option.descricao_cor}
                              </MenuItem>
                            ))}
                          {fieldName === "Local Armazenagem" &&
                            storageOptions.map((option, index) => (
                              <MenuItem key={index} value={option.id}>
                                {option.descricao_local}
                              </MenuItem>
                            ))}
                          {fieldName === "Modelo" &&
                            modelOptions.map((option, index) => (
                              <MenuItem key={index} value={option.id}>
                                {option.descricao_modelo}
                              </MenuItem>
                            ))}

                          {fieldName === "Ano / Modelo" &&
                            yearOptions.map((option, index) => (
                              <MenuItem key={index} value={option.id}>
                                {option.descricao_ano_modelo}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <FormTextField
                        label={fieldName}
                        name={fieldName}
                        value={formValues[fieldName as keyof typeof formValues]}
                        onChange={handleChange}
                        onUpdateVIN={(vin) => {
                          setFormValues((prevValues) => {
                            if (prevValues["V.I.N"] === vin) return prevValues;
                            return {
                              ...prevValues,
                              "V.I.N": vin,
                            };
                          });
                        }}
                        isEditing={false}
                        capturedImages={capturedImages}
                        setCapturedImages={setCapturedImages}
                        radioCapturedImages={radioCapturedImages}
                        setRadioCapturedImages={setRadioCapturedImages}
                        required
                        isPusherData={isPusherData}
                        inputProps={
                          fieldName === "V.I.N"
                            ? { maxLength: digitosChassi }
                            : {}
                        }
                        // disabled={isPusherData}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
            </Box>
            <LoadingButton
              variant="contained"
              fullWidth
              loading={loading}
              sx={{
                height: 50,
                marginTop: 2,
                backgroundColor: "#303030",
                borderRadius: 2,
                alignItems: "center",
                ":hover": {
                  bgcolor: "#5c5c5c",
                },
              }}
              onClick={handleSubmit}
            >
              <Typography>Cadastrar</Typography>
            </LoadingButton>
          </Container>
        </Box>
      </Box>

      {loading && (
        <Box
          position={"absolute"}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"center"}
          height={"100vh"}
          width={"100vw"}
          bgcolor={"#000"}
          sx={{
            opacity: 0.4,
            pointerEvents: "auto",
          }}
          zIndex={9999}
        >
          <CircularProgress size={100} />
        </Box>
      )}

      {openModal && (
        <CreateModal
          open={true}
          fieldName={openModal}
          onClose={handleModalClose}
          setCorOptions={setCorOptions}
          setModelOptions={setModelOptions}
          setYearOptions={setYearOptions}
          setLocalStorageOptions={setStorageOptions}
        />
      )}
    </ThemeProvider>
  );
};

export default NovoVeiculo;
